import { Muxer, ArrayBufferTarget } from 'webm-muxer'

import { pickWebCodecsWebmVideoConfig } from '@/rendering/export/adapters/webCodecsVp9Pick'
import { computeKeyframeInterval } from '@/rendering/export/exportPresets'
import type { EncoderFrameSink, ExportRenderConfig, FrameDescriptor } from '@/rendering/export/types'

/**
 * 是否具备 WebCodecs 视频编码所需的最小运行时 API（不保证具体 VP9 档位可用）。
 */
export function isWebCodecsWebmRuntimePresent(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined'
}

/**
 * 探测当前环境是否可用 WebCodecs + webm-muxer 离线导出（含 VP9/VP8 档位探测）。
 *
 * @param width - 输出宽
 * @param height - 输出高
 * @param fps - 输出帧率
 * @param quality - 导出质量（影响目标码率）
 * @param alpha - 是否需要 alpha 通道（透明导出）
 */
export async function probeWebCodecsWebmExport(params: {
  width: number
  height: number
  fps: number
  quality: ExportRenderConfig['quality']
  alpha?: boolean
}): Promise<boolean> {
  if (!isWebCodecsWebmRuntimePresent()) return false
  const picked = await pickWebCodecsWebmVideoConfig(
    params.width,
    params.height,
    params.fps,
    params.quality,
    params.alpha === true,
  )
  return picked !== null
}

/**
 * WebCodecs `VideoEncoder` + `webm-muxer` 的 WebM 导出：每帧显式 `timestamp`/`duration`（微秒），CFR 与离线管线对齐。
 *
 * 支持可选 alpha 通道（透明视频）：`new WebCodecsWebmEncoderSink({ alpha: true })`。
 */
export class WebCodecsWebmEncoderSink implements EncoderFrameSink {
  private readonly alpha: boolean
  private muxer: Muxer<ArrayBufferTarget> | null = null
  private target: ArrayBufferTarget | null = null
  private encoder: VideoEncoder | null = null
  private canvas: HTMLCanvasElement | null = null
  private fpsInt = 30
  private keyframeEvery = 60
  private aborted = false
  private encodeError: Error | null = null
  /** 与 `FrameDescriptor` 对齐的 CFR 时间戳（微秒），按序交给 `addVideoChunk` 第三参，避免编码器改写时间戳 */
  private pendingMuxTimestampsUs: number[] = []

  constructor(options: { alpha?: boolean } = {}) {
    this.alpha = options.alpha === true
  }

  /**
   * @inheritdoc
   */
  async beginEncoding(
    config: ExportRenderConfig,
    _totalFrames: number,
    canvas: HTMLCanvasElement,
  ): Promise<void> {
    this.aborted = false
    this.encodeError = null
    this.canvas = canvas
    /** 与 `frameTime` / `OfflineRenderOrchestrator` 中 `fpsInt` 一致 */
    this.fpsInt = Math.max(1, Math.round(Number(config.fps)))
    this.pendingMuxTimestampsUs = []

    const picked = await pickWebCodecsWebmVideoConfig(
      config.width,
      config.height,
      this.fpsInt,
      config.quality,
      this.alpha,
    )
    if (!picked) {
      const reason = this.alpha ? 'VP9 alpha' : 'VP9/VP8'
      throw new Error(`当前环境不支持 WebCodecs ${reason} 导出配置`)
    }
    this.target = new ArrayBufferTarget()
    this.muxer = new Muxer({
      target: this.target,
      video: {
        codec: picked.matroskaCodec,
        width: config.width,
        height: config.height,
        frameRate: this.fpsInt,
        alpha: picked.alpha,
      },
      firstTimestampBehavior: 'strict',
    })

    this.encoder = new VideoEncoder({
      output: (chunk, meta) => {
        try {
          const tsUs = this.pendingMuxTimestampsUs.shift()
          if (tsUs === undefined) {
            this.encodeError = new Error('WebM 多路复用：输出块多于预期时间戳')
            return
          }
          this.muxer?.addVideoChunk(chunk, meta, tsUs)
        } catch (e) {
          this.encodeError = e instanceof Error ? e : new Error(String(e))
        }
      },
      error: (e) => {
        this.encodeError = e
      },
    })

    this.encoder.configure({
      codec: picked.codec,
      width: config.width,
      height: config.height,
      bitrate: picked.bitrate,
      framerate: this.fpsInt,
      latencyMode: 'quality',
      bitrateMode: 'variable',
      ...(picked.alpha ? { alpha: 'keep' as const } : {}),
    })

    this.keyframeEvery = computeKeyframeInterval(this.fpsInt)
  }

  /**
   * @inheritdoc
   */
  async consumeFrame(desc: FrameDescriptor): Promise<void> {
    if (this.aborted || !this.encoder || !this.canvas) return
    if (this.encodeError) throw this.encodeError

    /** 与离线管线 `FrameDescriptor` / `frameTime` 一致 */
    const fps = Math.max(1, Math.round(Number(desc.fps) || this.fpsInt))
    const timestampUs = Math.round((desc.frameIndex * 1_000_000) / fps)
    const durationUs = Math.round(1_000_000 / fps)
    const keyFrame = desc.frameIndex % this.keyframeEvery === 0

    this.pendingMuxTimestampsUs.push(timestampUs)

    let frame: VideoFrame | null = null
    try {
      frame = new VideoFrame(this.canvas, {
        timestamp: timestampUs,
        duration: durationUs,
      })
      this.encoder.encode(frame, { keyFrame })
    } finally {
      frame?.close()
    }

    /** 保证本帧编码输出已回调，且与 `pendingMuxTimestampsUs` 一一对应 */
    await this.encoder.flush()

    if (this.pendingMuxTimestampsUs.length > 0) {
      throw new Error('WebM 编码：flush 后仍有未消耗的 CFR 时间戳（编码输出与预期不一致）')
    }

    if (this.encodeError) throw this.encodeError
  }

  /**
   * @inheritdoc
   */
  async finishEncoding(): Promise<Blob | null> {
    const enc = this.encoder
    const mux = this.muxer
    const tgt = this.target

    this.encoder = null
    this.muxer = null
    this.canvas = null
    if (this.aborted || !enc || !mux || !tgt) {
      this.pendingMuxTimestampsUs = []
      try {
        enc?.close()
      } catch {
        /* ignore */
      }
      return null
    }

    if (this.encodeError) {
      try {
        enc.close()
      } catch {
        /* ignore */
      }
      throw this.encodeError
    }

    try {
      await enc.flush()
    } catch (e) {
      try {
        enc.close()
      } catch {
        /* ignore */
      }
      throw e instanceof Error ? e : new Error(String(e))
    }

    try {
      enc.close()
    } catch {
      /* ignore */
    }

    if (this.encodeError) throw this.encodeError

    mux.finalize()
    this.pendingMuxTimestampsUs = []
    const buf = tgt.buffer
    if (!buf || buf.byteLength === 0) return null
    return new Blob([buf], { type: 'video/webm' })
  }

  /**
   * @inheritdoc
   */
  async abortEncoding(): Promise<void> {
    this.aborted = true
    try {
      await this.encoder?.flush()
    } catch {
      /* ignore */
    }
    try {
      this.encoder?.close()
    } catch {
      /* ignore */
    }
    this.encoder = null
    this.muxer = null
    this.target = null
    this.canvas = null
    this.pendingMuxTimestampsUs = []
  }
}
